import {
    TResult,
    makeSuccess,
    makeFailure,
    base64ToJson,
    jsonToBase64,
    ISgApiService,
    IEndpoint,
} from "../../../sg-core";

import ObservationMapper, { NewObservationMapper } from "../mappers/ObservationMapper";
import IObservation, {
    INewObservation,
    IObservationListSearch,
    IObservationCounters,
    IProcessForm,
    EObservationStatus,
} from "../interfaces/IObservation";
import { ContextMapper } from "../mappers/ContextMapper";
import { IUnparsedContext } from "../interfaces/IHavainnotContext";
import { IInitialContextResult } from "./contextService";
import { IPagination } from "../interfaces/common/IPagination";
import { IObservationFilters } from "../store/reducers/observationListReducer";
import { IObservationPackage } from "../interfaces/ObservationPackage/ObservationPackage";
import {
    IObservationEvent,
    IObservationEventDTO,
    ObservationEventMapper,
    TCreateObservationEvent,
} from "../interfaces/IObservationEvent";
import { extractFormDataIdentifiersDTO, pacakgeToIdentifiers } from "../utils/id.helper";

export interface IObservationService {
    getObservations(
        search?: IObservationFilters,
    ): Promise<TResult<{ observations: IObservation[]; context: IUnparsedContext }>>;
    getObservationPage(
        pagination: IPagination,
        search: IObservationFilters,
    ): Promise<TResult<{ totalItems: number; observations: IObservation[]; context: IUnparsedContext }>>;
    getObservationCounts(search?: IObservationFilters): Promise<TResult<IObservationCounters>>;
    getObservationsMap(
        search?: IObservationFilters,
    ): Promise<TResult<{ observations: IObservation[]; context: IUnparsedContext }>>;

    getObservation(
        observationGuid: string | number,
    ): Promise<TResult<{ observation: IObservation; context: IUnparsedContext }>>;

    saveObservation(
        observation: IObservation,
    ): Promise<TResult<{ observation: IObservation; eventId: number }>>;
    createObservation(
        observation: INewObservation,
    ): Promise<TResult<{ observation: IObservation; event: { id: string } }>>;

    delegateObservation: (
        opts: IDelegateObservationOptions,
    ) => Promise<TResult<{ observation: IObservation }>>;

    saveHistoryData: (jsonData: IObservationPackage) => Promise<TResult<{ id: string }>>;
    getHistoryData: (id: string) => Promise<TResult<IObservationPackage>>;
    getHistoryDataAnonym: (id: string) => Promise<TResult<{ data: IObservationPackage; token: string }>>;

    createHistoryEvent: (event: TCreateObservationEvent) => Promise<TResult<IObservationEvent>>;

    getHistoryEvents: (observationId: number) => Promise<TResult<Array<IObservationEvent>>>;
    updateHistoryEvent: (
        eventId: number | string,
        data: Partial<TCreateObservationEvent>,
    ) => Promise<TResult<IObservationEvent>>;
}

export interface IObservationResult {
    success: true;
    action: "hae_havainto";
    havainto: {
        id: number;
        guid: string;
        // nimi: string;
        otsikko: string;
        kuvaus: string;
        kuva?: string;
        kirjattu: string;
        kirjaaja: number;
        lomake: number;
        alue: string;
        tyo?: number | undefined;
        tila: string;
        lomakedata: string;
        on_julkinen: "Y" | "N";
        on_kasittelija?: "Y" | "N";
        karttasijainti?: string;
        kasittelija?: number;
        luokitteluteksti?: string;
        kasittelylomake_jsonschema?: string;
        lomakedatahaku: string;
    };
    tapahtuma: { id: number };
    // lomakemaaritykset: any[] comes without base64 schema data

    henkilot: IInitialContextResult["henkilot"];
    lomakkeet: IInitialContextResult["lomakkeet"];
    kategoriat: IInitialContextResult["kategoriat"];
}

export interface IObservationListResult {
    success: true;
    action: "tallenna_havainto";
    havainnot?: any[];
    haku: { tietuemaara: number };
    henkilot: IInitialContextResult["henkilot"];
    lomakkeet: IInitialContextResult["lomakkeet"];
    kategoriat: IInitialContextResult["kategoriat"];
}
export interface IObservationCountsByStatusResult {
    success: true;
    action: "hae_havaintojen_maarat_tiloittain";
    havainnot: {
        uusi: { tila: "uusi"; maara: number };
        kasittelyssa: { tila: "kasittelyssa"; maara: number };
        valmis: { tila: "valmis"; maara: number };
        kasitelty: { tila: "kasitelty"; maara: number };
    };
}
export interface ICreateObservationBody {
    success: true;
    action: "tallenna_havainto";
    havainto: {
        // id: number;
        // nimi: string;
        otsikko: string;
        kuvaus: string;
        kuva?: string;
        kirjattu: string;
        lomakedatahaku: string;
        //  kirjaaja: number;
        lomake: number;
        alue: number;
        tyo?: number;
        tila: string;
        lomakedata: string;
        on_julkinen: "Y" | "N";
        karttasijainti?: string;
        luokitteluteksti?: string;
        kasittelylomake_jsonschema?: string;
    };
}

export type IDelegateObservationOptions = {
    loggedInUser: number;
    reciever: number;
    observationGuid: IObservation["guid"];
};
export type TGetObservationPageValue = TResult<{
    totalItems: number;
    observations: IObservation[];
    context: IUnparsedContext;
}>;

export interface IObservationService {
    getObservations(
        search?: IObservationFilters,
    ): Promise<TResult<{ observations: IObservation[]; context: IUnparsedContext }>>;
    getObservationPage(
        pagination: IPagination,
        search: IObservationFilters,
    ): Promise<TResult<{ totalItems: number; observations: IObservation[]; context: IUnparsedContext }>>;
    getObservationCounts(search?: IObservationFilters): Promise<TResult<IObservationCounters>>;
    getObservationsMap(
        search?: IObservationFilters,
    ): Promise<TResult<{ observations: IObservation[]; context: IUnparsedContext }>>;

    getObservation(
        observationGuid: string | number,
    ): Promise<TResult<{ observation: IObservation; context: IUnparsedContext }>>;

    saveObservation(
        observation: IObservation,
    ): Promise<TResult<{ observation: IObservation; eventId: number }>>;
    createObservation(
        observation: INewObservation,
    ): Promise<TResult<{ observation: IObservation; event: { id: string } }>>;

    delegateObservation: (
        opts: IDelegateObservationOptions,
    ) => Promise<TResult<{ observation: IObservation }>>;

    saveHistoryData: (jsonData: IObservationPackage) => Promise<TResult<{ id: string }>>;
    getHistoryData: (id: string) => Promise<TResult<IObservationPackage>>;
    getHistoryDataAnonym: (id: string) => Promise<TResult<{ data: IObservationPackage; token: string }>>;

    createHistoryEvent: (event: TCreateObservationEvent) => Promise<TResult<IObservationEvent>>;

    getHistoryEvents: (observationId: number) => Promise<TResult<Array<IObservationEvent>>>;
    updateHistoryEvent: (
        eventId: number | string,
        data: Partial<TCreateObservationEvent>,
    ) => Promise<TResult<IObservationEvent>>;
}

export const getObservationService = (
    apiService: ISgApiService,
    Endpoint: IEndpoint,
    anonymousSession___: boolean,
): IObservationService => {
    let anonymousSession = window.location.pathname.indexOf("anonyymi") > -1;

    const makeAnonymousAction = (actionName: string) =>
        anonymousSession ? `${actionName}_anonyymi` : actionName;

    const Action: { [K in keyof IObservationService]: string } = {
        getObservations: "listaa_havainnot",
        getObservationsMap: "listaa_kartta_havainnot",
        getObservationPage: "listaa_havainnot",
        getObservationCounts: "hae_havaintojen_maarat_tiloittain",

        getObservation: makeAnonymousAction("hae_havainto"),
        saveObservation: "tallenna_havainto",
        createObservation: makeAnonymousAction("lisaa_havainto"),
        delegateObservation: "delegoi_havainto",
        saveHistoryData: makeAnonymousAction("tallenna_historiapaketti"),
        getHistoryData: "hae_historiapaketti",
        getHistoryDataAnonym: "hae_historiapaketti_anonyymi",
        createHistoryEvent: "lisaa_tapahtuma",
        updateHistoryEvent: makeAnonymousAction("tallenna_tapahtuma"),
        getHistoryEvents: "hae_historia",
    };

    const getObservations = async (
        search?: IObservationListSearch,
    ): Promise<TResult<{ observations: IObservation[]; context: IUnparsedContext }>> => {
        const action = Action.getObservations;

        const body = {
            haku: {
                kategoria: search?.category,
                tila: search?.status,
                roolilista: search?.roles?.join(","),

                sivupituus: 999,
            },
        };
        const apiResponse = await apiService.sendAction<IObservationListResult>(Endpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const havainnot = apiResponse.value.havainnot || [];
            return makeSuccess({
                observations: havainnot.map(ObservationMapper.apiToDomain),
                context: ContextMapper.apiToDomain(apiResponse.value),
            });
        }

        return apiResponse;
    };

    const getObservationsMap = async (
        search?: IObservationFilters,
    ): Promise<TResult<{ observations: IObservation[]; context: IUnparsedContext }>> => {
        const action = Action.getObservationsMap;

        const body = {
            haku: {
                kategoria: search?.category,
                tila: search?.status,
                roolilista: search?.roles?.join(","),
                sijainti: search?.region,
                kohde: search?.target,
                alue: search?.area,
                sivupituus: 999,
            },
        };
        const apiResponse = await apiService.sendAction<IObservationListResult>(Endpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const havainnot = apiResponse.value.havainnot || [];
            return makeSuccess({
                observations: havainnot.map(ObservationMapper.apiToDomain),
                context: ContextMapper.apiToDomain(apiResponse.value),
            });
        }

        return apiResponse;
    };

    /**
     * Fetches a single page of observations according to the given pagination &
     * search filtering oprions
     * @param pagination Object with page & pageSize
     * @param search Filters to use in the search
     */
    const getObservationPage = async (
        pagination: IPagination,
        search?: IObservationFilters,
    ): Promise<TGetObservationPageValue> => {
        const action = Action.getObservationPage;

        const body = {
            haku: {
                // Set up pagination
                sivunumero: pagination.page + 1, // Gate pagination is 1-indexed, the app uses 0-indexed
                sivupituus: pagination.pageSize,

                // Optional filters
                tila: search?.status,
                teksti: search?.freeText,

                sijainti: search?.region,
                kohde: search?.target,
                alue: search?.area,

                roolilista: search?.roles.join(","),
                kirjaaja: search?.creator,
                kasittelija: search?.handler,
                kategoria: search?.category,
                arkistorajaus: search?.includeArchived ? "N" : "Y",
            },
        };

        // Fetch the data
        const apiResponse = await apiService.sendAction<IObservationListResult>(Endpoint, action, body);

        if (apiResponse.isSuccess === true) {
            const { havainnot, haku } = apiResponse.value;

            // Map the fetched observation list and the data context into domain objects
            const observations = (havainnot || []).map(ObservationMapper.apiToDomain);
            const context = ContextMapper.apiToDomain(apiResponse.value);

            const totalItems = haku.tietuemaara;

            return makeSuccess({
                totalItems,
                observations,
                context,
            });
        }

        return makeFailure(apiResponse.error);
    };

    const getObservationCounts = async (
        search?: IObservationListSearch,
    ): Promise<TResult<IObservationCounters>> => {
        const action = Action.getObservationCounts;
        const body = {
            haku: {
                roolilista: search?.roles?.join(","),
                kirjaaja: search?.creator,
                kasittelija: search?.handler,
            },
        };
        const apiResponse = await apiService.sendAction<IObservationCountsByStatusResult>(
            Endpoint,
            action,
            body,
        );

        if (apiResponse.isSuccess === true) {
            const { havainnot } = apiResponse.value;
            return makeSuccess({
                new: havainnot.uusi.maara,
                onHandling: havainnot.kasittelyssa.maara,
                ready: havainnot.kasitelty.maara,
            });
        }

        return apiResponse;
    };

    const getObservation = async (
        observationGuid?: string | number,
    ): Promise<TResult<{ observation: IObservation; context: IUnparsedContext }>> => {
        const action = Action.getObservation;
        const body = {
            havainto: {
                guid: typeof observationGuid === "string" ? observationGuid : undefined,
                id: typeof observationGuid === "number" ? observationGuid : undefined,
            },
        };

        const apiResponse = await apiService.sendAction<IObservationResult>(Endpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                observation: ObservationMapper.apiToDomain(apiResponse.value.havainto),
                context: ContextMapper.apiToDomain(apiResponse.value),
            });
        } else if (
            process.env.NODE_ENV === "development" &&
            ((apiResponse as any).error?.error?.message || (apiResponse as any).error?.message).startsWith(
                "tunnisterekisterikentän sisältö puuttuu",
            )
        ) {
            return makeSuccess({
                missingIdRegister: true,
                //@ts-ignore
                observation: ObservationMapper.apiToDomain(
                    //@ts-ignore
                    apiResponse.error.havainto || apiResponse.error.data.havainto,
                ),
                //@ts-ignore
                context: ContextMapper.apiToDomain(apiResponse.error),
            });
        }
        return apiResponse;
    };

    const saveObservation = async (
        observation: IObservation,
    ): Promise<TResult<{ observation: IObservation; eventId: number }>> => {
        const action = Action.saveObservation;
        if (!observation.formSchema) {
            throw Error("Missing form configurations");
        }

        const body = {
            havainto: ObservationMapper.domainToApi(observation),
            tunnisteet: extractFormDataIdentifiersDTO(observation.formData, observation.formSchema),
        };

        const apiResponse = await apiService.sendAction<IObservationResult>(Endpoint, action, body);
        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                observation: ObservationMapper.apiToDomain(apiResponse.value.havainto),
                eventId: apiResponse.value.tapahtuma.id,
            });
        }
        return apiResponse;
    };

    const createObservation: IObservationService["createObservation"] = async (
        observation: INewObservation,
    ) => {
        const action = Action.createObservation;
        if (!observation.formSchema) {
            throw Error("Missing form configurations");
        }

        const body = {
            havainto: NewObservationMapper.domainToApi(observation),
            tunnisteet: extractFormDataIdentifiersDTO(observation.formData, observation.formSchema),
        };
        const apiResponse = await apiService.sendAction<IObservationResult>(Endpoint, action, body);
        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                observation: ObservationMapper.apiToDomain(apiResponse.value.havainto),
                event: apiResponse.value.tapahtuma as any,
            });
        }
        return apiResponse;
    };

    const delegateObservation = async (
        opts: IDelegateObservationOptions,
    ): Promise<TResult<{ observation: IObservation }>> => {
        const delegateAction = Action.delegateObservation;
        const delegateBody = { havainto: { guid: opts.observationGuid, kasittelija: opts.reciever } };

        // create marking about the delegation
        const getObservationResponse = await getObservation(opts.observationGuid);
        if (!getObservationResponse.isSuccess) throw getObservationResponse.error;
        const observation = { ...getObservationResponse.value.observation, editor: opts.reciever };

        const markingAboutDelegation: IProcessForm = {
            observationId: observation.id,
            creator: opts.loggedInUser,
            created_at: new Date().toISOString(),
            formType: 0,
            formData: { valitetty: true, vastaanottaja: opts.reciever },
        };
        observation.formData.tila = EObservationStatus.InProgress;
        observation.formData.merkinnat = (observation.formData.merkinnat || []).concat(
            markingAboutDelegation,
        );

        const updateObservationResponse = await saveObservation(observation);
        if (!updateObservationResponse.isSuccess) throw updateObservationResponse.error;

        const deleagateResponse = await apiService.sendAction<IObservationResult>(
            Endpoint,
            delegateAction,
            delegateBody,
        );

        if (deleagateResponse.isSuccess === true) {
            return makeSuccess({
                observation: ObservationMapper.apiToDomain(deleagateResponse.value.havainto),
            });
        }

        return deleagateResponse;
    };

    const saveHistoryData: IObservationService["saveHistoryData"] = async (jsonData) => {
        const action = Action.saveHistoryData;
        const body = {
            base64Data: jsonToBase64(jsonData),
            tunnisteet: pacakgeToIdentifiers(jsonData),
        };

        const apiResponse = await apiService.sendAction<any>(Endpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess({ id: apiResponse.value.id });
        }
        return apiResponse;
    };

    const getHistoryData: IObservationService["getHistoryData"] = async (id) => {
        const action = Action.getHistoryData;
        const body = { id };

        const apiResponse = await apiService.sendAction<any>(Endpoint, action, body);

        if (apiResponse.isSuccess === true) {
            try {
                const data = base64ToJson(apiResponse.value.base64data);
                return makeSuccess(data);
            } catch (err) {
                return makeFailure(err as Error);
            }
        }
        return apiResponse;
    };

    const getHistoryDataAnonym: IObservationService["getHistoryDataAnonym"] = async (id) => {
        const action = Action.getHistoryDataAnonym;
        const body = { id };
        const noToken = true;
        const apiResponse = await apiService.sendAction<any>(Endpoint, action, body, noToken);

        if (apiResponse.isSuccess === true) {
            try {
                const data = base64ToJson(apiResponse.value.base64data);
                return makeSuccess({ data, token: apiResponse.value.sid });
            } catch (err) {
                return makeFailure(err as Error);
            }
        }
        return apiResponse;
    };

    const createHistoryEvent: IObservationService["createHistoryEvent"] = async (event) => {
        const action = Action.createHistoryEvent;
        const payload = {
            havainto: { id: event.observation },
            tapahtuma: ObservationEventMapper.domainToCreatableApi(event),
        };
        const response = await apiService.sendAction<{ tapahtuma: IObservationEventDTO }>(
            Endpoint,
            action,
            payload,
        );
        if (response.isSuccess) {
            return makeSuccess(ObservationEventMapper.apiToDomain(response.value.tapahtuma));
        }
        return response;
    };

    const updateHistoryEvent: IObservationService["updateHistoryEvent"] = async (eventId, data) => {
        const action = Action.updateHistoryEvent;
        let reqBody: any = {
            tapahtuma: {
                id: eventId,
                ...ObservationEventMapper.domainToCreatableApi(data),
            },
        };

        if (data.formData && data.formSchema) {
            reqBody = {
                ...reqBody,
                tunnisteet: extractFormDataIdentifiersDTO(data.formData, data.formSchema),
            };
        }

        const apiResponse = await apiService.sendAction<{ tapahtuma: IObservationEventDTO }>(
            Endpoint,
            action,
            reqBody,
        );
        if (apiResponse.isSuccess) {
            return makeSuccess(ObservationEventMapper.apiToDomain(apiResponse.value.tapahtuma));
        }

        return apiResponse;
    };

    const getHistoryEvents: IObservationService["getHistoryEvents"] = async (observationId) => {
        const action = Action.getHistoryEvents;
        const payload = { havainto: { id: observationId } };

        const response = await apiService.sendAction<{
            havainto: { tapahtumat: Array<IObservationEventDTO> };
        }>(Endpoint, action, payload);
        if (response.isSuccess) {
            return makeSuccess(
                (response.value.havainto.tapahtumat || []).map(ObservationEventMapper.apiToDomain),
            );
        }
        return response;
    };

    return {
        getObservations,
        getObservationPage,
        getObservation,
        getObservationCounts,
        getObservationsMap,

        //  markForEditing,
        //   unmarkFromEditing,

        saveObservation,
        createObservation,
        delegateObservation,

        saveHistoryData,
        getHistoryData,
        getHistoryDataAnonym,

        updateHistoryEvent,
        getHistoryEvents,
        createHistoryEvent,
    };
};
